import React from 'react';
import styled, { css } from 'styled-components';
import { spacing } from '../../style';
import {
  CssUnitsAsResponsiveObject,
  getCssRuleWithResponsiveCalculation,
} from '../../utils/get-css-rule-with-responsive-calc';

export interface GroupProps {
  flexWrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  flexDirection?: CssUnitsAsResponsiveObject<React.CSSProperties['flexDirection']>;
  alignItems?: React.CSSProperties['alignItems'];
  bottomItemMargin?: keyof typeof spacing;
  sideItemsMargin?: keyof typeof spacing;
  justifyContent?: string;
  block?: boolean;
  children?: React.ReactNode;
  role?: 'tablist' | 'tabpanel' | 'navigation';
  overflowY?: 'auto';
  overflowX?: 'auto';
  gap?: keyof typeof spacing;
  height?: string;
  as?: 'ul';
}

const StyledGroup = styled.div<GroupProps>`
  display: flex;

  ${props =>
    getCssRuleWithResponsiveCalculation<React.CSSProperties['flexDirection']>({
      property: 'flex-direction',
      value: props.flexDirection,
    })};
  ${props =>
    props.flexWrap &&
    css`
      flex-wrap: ${props.flexWrap || 'nowrap'};
    `};
  ${props =>
    props.gap &&
    css`
      gap: ${spacing[props.gap]};
    `};

  ${props =>
    props.alignItems &&
    css`
      align-items: ${props.alignItems};
    `};

  ${props =>
    props.justifyContent &&
    css`
      justify-content: ${props.justifyContent};
    `};

  ${props =>
    props.block &&
    css`
      width: 100%;
    `};
  ${props =>
    props.height &&
    css`
      height: ${props.height};
    `};
  ${props =>
    props.overflowY &&
    css`
      overflow-y: ${props.overflowY};
    `};
  ${props =>
    props.overflowX &&
    css`
      overflow-x: ${props.overflowX};
    `};

  > *:not(:only-child) {
    margin-right: ${props => spacing[props.sideItemsMargin || 'xs']};
  }

  > * {
    margin-bottom: ${props => spacing[props.bottomItemMargin || 'none']};
  }

  > *:last-child {
    margin-right: 0;
  }
`;

// Must wrap 'styled components' output for defaultProps to work. -> https://stackoverflow.com/questions/52226596/styled-components-defaultprops
const Group = (props: GroupProps) => {
  return <StyledGroup {...props} />;
};

export default Group;
